import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { problemResponse } from './common.js';

const kopecks = z.number().int().min(0).max(100_000_000);

/** Why a person changed something by hand (stored in the audit log; invariant 5). */
export const ReasonSchema = z.string().trim().min(1).max(500);

export const CategorySchema = z
  .object({
    id: z.uuid(),
    parentId: z.uuid().nullable(),
    name: z.string(),
    path: z.string().meta({ example: 'Обличчя / Сироватки' }),
    wooCategoryId: z.number().int().nullable(),
    productCount: z.number().int().meta({ description: 'Products directly in this category' }),
  })
  .meta({ id: 'Category' });

export const CreateCategorySchema = z
  .object({ name: z.string().max(120), parentId: z.uuid().nullable().optional() })
  .meta({ id: 'CreateCategory' });

export const UpdateCategorySchema = z
  .object({
    name: z.string().max(120).optional(),
    parentId: z
      .uuid()
      .nullable()
      .optional()
      .meta({ description: 'null moves it to the top level' }),
  })
  .meta({ id: 'UpdateCategory' });

export const StockSchema = z
  .object({
    onHand: z.number().int(),
    reserved: z.number().int(),
    available: z
      .number()
      .int()
      .meta({ description: 'onHand − reserved (negative after a short count)' }),
  })
  .meta({ id: 'Stock' });

export const ProductImageSchema = z
  .object({
    id: z.uuid(),
    url: z.string().meta({ example: '/api/v1/catalogue/images/…' }),
    contentType: z.string(),
    byteSize: z.number().int(),
  })
  .meta({ id: 'ProductImage' });

export const ReorderImagesSchema = z
  .object({ imageIds: z.array(z.uuid()).max(20) })
  .meta({ id: 'ReorderImages' });

export const ProductSchema = z
  .object({
    id: z.uuid(),
    sku: z.string(),
    name: z.string(),
    categoryId: z.uuid().nullable(),
    categoryPath: z.string().nullable(),
    wooProductId: z.number().int().nullable(),
    wooVariationId: z.number().int().nullable(),
    costPrice: kopecks.nullable().meta({ description: 'Kopecks; null when unknown' }),
    basePrice: kopecks.meta({ description: 'Retail list price in kopecks' }),
    isActive: z.boolean(),
    stock: StockSchema,
    heroImage: ProductImageSchema.nullable(),
    images: z.array(ProductImageSchema).meta({ description: 'Extra images, in display order' }),
    wooImages: z.array(z.string()).meta({
      description:
        "The store's image URLs (main first), synced from Woo: shown unless the product has its own hero image",
    }),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'Product' });

export const WooProductSyncSchema = z
  .object({
    name: z.string().max(300),
    wooProductId: z.number().int().positive(),
    wooVariationId: z
      .number()
      .int()
      .positive()
      .nullable()
      .meta({ description: 'Set for a variation (flattened to its own SKU)' }),
    categories: z
      .array(z.object({ wooId: z.number().int().positive(), name: z.string().max(200) }))
      .max(10)
      .meta({ description: 'Category path from the top, each level with its Woo term id' }),
    images: z.array(z.string().max(2000)).max(21).meta({ description: 'Image URLs, main first' }),
    regularPrice: kopecks.nullable().meta({
      description: "Woo's regular price: used only as a new product's opening base price",
    }),
    published: z.boolean().meta({ description: 'A new product goes on sale only if published' }),
  })
  .meta({ id: 'WooProductSync' });

export const WooProductSyncResultSchema = z
  .object({
    productId: z.uuid(),
    sku: z.string(),
    created: z.boolean(),
    changed: z.boolean().meta({ description: 'Anything the store owns changed' }),
    stock: StockSchema,
  })
  .meta({ id: 'WooProductSyncResult' });

export const ProductPageSchema = z
  .object({ items: z.array(ProductSchema), total: z.number().int() })
  .meta({ id: 'ProductPage' });

const booleanQuery = z.enum(['true', 'false']).transform((v) => v === 'true');

export const ProductQuerySchema = z.object({
  search: z.string().max(100).optional(),
  categoryId: z.uuid().optional(),
  active: booleanQuery.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export const UpdateProductSchema = z
  .object({
    name: z.string().max(300).optional(),
    categoryId: z.uuid().nullable().optional().meta({ description: 'null = no category' }),
    basePrice: kopecks.optional(),
    costPrice: kopecks.nullable().optional(),
    isActive: z.boolean().optional(),
    reason: ReasonSchema,
  })
  .meta({ id: 'UpdateProduct' });

export const CreateProductSchema = z
  .object({
    sku: z.string().max(64),
    name: z.string().max(300),
    categoryPath: z
      .string()
      .max(800)
      .nullable()
      .optional()
      .meta({ description: 'e.g. "Обличчя / Сироватки"; missing categories are created' }),
    basePrice: kopecks,
    costPrice: kopecks.nullable().optional(),
    isActive: z.boolean().optional(),
    onHand: z
      .number()
      .int()
      .min(0)
      .max(1_000_000_000)
      .optional()
      .meta({ description: 'Opening stock' }),
  })
  .meta({ id: 'CreateProduct' });

export const CountStockSchema = z
  .object({ onHand: z.number().int().min(0).max(1_000_000_000), reason: ReasonSchema })
  .meta({ id: 'CountStock' });

export const ImportCatalogueSchema = z
  .object({
    csv: z.string().min(1).max(2_000_000).meta({
      description:
        'CSV text: sku, name, base_price; optional category, cost_price, is_active, on_hand',
    }),
    dryRun: z.boolean().default(false),
  })
  .meta({ id: 'ImportCatalogue' });

export const ImportResultSchema = z
  .object({
    applied: z.boolean(),
    created: z.number().int(),
    updated: z.number().int(),
    unchanged: z.number().int(),
    newCategories: z.array(z.string()),
    errors: z.array(z.object({ line: z.number().int(), message: z.string() })),
  })
  .meta({ id: 'ImportResult' });

export type WooProductSyncRequest = z.infer<typeof WooProductSyncSchema>;
export type WooProductSyncResultDto = z.infer<typeof WooProductSyncResultSchema>;
export type CategoryDto = z.infer<typeof CategorySchema>;
export type ProductImageDto = z.infer<typeof ProductImageSchema>;
export type ReorderImagesRequest = z.infer<typeof ReorderImagesSchema>;
export type CreateCategoryRequest = z.infer<typeof CreateCategorySchema>;
export type UpdateCategoryRequest = z.infer<typeof UpdateCategorySchema>;
export type ProductDto = z.infer<typeof ProductSchema>;
export type ProductPageDto = z.infer<typeof ProductPageSchema>;
export type ProductQuery = z.infer<typeof ProductQuerySchema>;
export type CreateProductRequest = z.infer<typeof CreateProductSchema>;
export type UpdateProductRequest = z.infer<typeof UpdateProductSchema>;
export type CountStockRequest = z.infer<typeof CountStockSchema>;
export type ImportCatalogueRequest = z.input<typeof ImportCatalogueSchema>;
export type ImportResultDto = z.infer<typeof ImportResultSchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });
const body = <T extends z.ZodType>(schema: T) => ({ required: true, content: json(schema) });
const unauthorized = problemResponse('Not signed in');
const forbidden = problemResponse('Missing permission');
const idParam = { path: z.object({ id: z.uuid() }) };

const binary = z.string().meta({ format: 'binary' });
const imageBody = {
  'image/jpeg': { schema: binary },
  'image/png': { schema: binary },
  'image/webp': { schema: binary },
};
const imageResponses = {
  '200': { description: 'The product with its images', content: json(ProductSchema) },
  '400': problemResponse('Not a JPEG / PNG / WebP image, empty, or over 5 MB'),
  '401': unauthorized,
  '403': forbidden,
  '404': problemResponse('No such product or image'),
};

export const cataloguePaths: ZodOpenApiPathsObject = {
  '/api/v1/catalogue/categories': {
    get: {
      summary: 'Category tree as a flat list (with paths)',
      responses: {
        '200': { description: 'OK', content: json(z.array(CategorySchema)) },
        '401': unauthorized,
      },
    },
    post: {
      summary: 'Create a category (admin)',
      requestBody: body(CreateCategorySchema),
      responses: {
        '201': { description: 'Created', content: json(CategorySchema) },
        '400': problemResponse('Invalid name or unknown parent'),
        '401': unauthorized,
        '403': forbidden,
        '409': problemResponse('Same name under the same parent'),
        '422': problemResponse('Too deep'),
      },
    },
  },
  '/api/v1/catalogue/categories/{id}': {
    patch: {
      summary: 'Rename and / or move a category (admin)',
      requestParams: idParam,
      requestBody: body(UpdateCategorySchema),
      responses: {
        '200': { description: 'Updated', content: json(CategorySchema) },
        '400': problemResponse('Invalid name or unknown parent'),
        '401': unauthorized,
        '403': forbidden,
        '404': problemResponse('No such category'),
        '409': problemResponse('Same name under the same parent'),
        '422': problemResponse('Move would create a cycle or exceed the depth limit'),
      },
    },
    delete: {
      summary: 'Delete an empty category (admin)',
      requestParams: idParam,
      responses: {
        '204': { description: 'Deleted' },
        '401': unauthorized,
        '403': forbidden,
        '404': problemResponse('No such category'),
        '422': problemResponse('Category still has subcategories or products'),
      },
    },
  },
  '/api/v1/catalogue/products': {
    get: {
      summary: 'Search products by SKU or name; filter by category (with subcategories)',
      requestParams: { query: ProductQuerySchema },
      responses: {
        '200': { description: 'OK', content: json(ProductPageSchema) },
        '400': problemResponse('Invalid query'),
        '401': unauthorized,
      },
    },
    post: {
      summary: 'Add a product by hand (admin; until the Woo sync)',
      requestBody: body(CreateProductSchema),
      responses: {
        '201': { description: 'Created', content: json(ProductSchema) },
        '400': problemResponse('Invalid product'),
        '401': unauthorized,
        '403': forbidden,
        '409': problemResponse('SKU already in the catalogue'),
      },
    },
  },
  '/api/v1/catalogue/products/{id}': {
    get: {
      summary: 'One product with stock',
      requestParams: idParam,
      responses: {
        '200': { description: 'OK', content: json(ProductSchema) },
        '401': unauthorized,
        '404': problemResponse('No such product'),
      },
    },
    patch: {
      summary:
        'Edit name, category, base / cost price or the active flag (reason required, audited)',
      requestParams: idParam,
      requestBody: body(UpdateProductSchema),
      responses: {
        '200': { description: 'Updated', content: json(ProductSchema) },
        '400': problemResponse('Invalid price or missing reason'),
        '401': unauthorized,
        '403': forbidden,
        '404': problemResponse('No such product'),
      },
    },
  },
  '/api/v1/catalogue/products/{sku}': {
    put: {
      summary:
        "Store product sync (DeliStore plugin, signed): create or update a SKU from Woo — name, category path, Woo ids, images; prices and stock stay the CRM's",
      requestParams: { path: z.object({ sku: z.string() }) },
      requestBody: body(WooProductSyncSchema),
      responses: {
        '200': { description: 'Synced', content: json(WooProductSyncResultSchema) },
        '400': problemResponse('Invalid product data'),
        '401': problemResponse('Not signed by the store'),
        '403': forbidden,
      },
    },
  },
  '/api/v1/catalogue/products/{id}/stock': {
    put: {
      summary: 'Stock count: set the quantity on hand (reason required, audited)',
      requestParams: idParam,
      requestBody: body(CountStockSchema),
      responses: {
        '200': { description: 'Counted', content: json(ProductSchema) },
        '400': problemResponse('Invalid quantity or missing reason'),
        '401': unauthorized,
        '403': forbidden,
        '404': problemResponse('No such product'),
      },
    },
  },
  '/api/v1/catalogue/products/{id}/images/hero': {
    put: {
      summary: 'Upload the hero image (replaces the current one). Body: the file itself',
      requestParams: idParam,
      requestBody: { required: true, content: imageBody },
      responses: imageResponses,
    },
  },
  '/api/v1/catalogue/products/{id}/images': {
    post: {
      summary: 'Add an extra image at the end (max 20). Body: the file itself',
      requestParams: idParam,
      requestBody: { required: true, content: imageBody },
      responses: { ...imageResponses, '422': problemResponse('Too many extra images') },
    },
  },
  '/api/v1/catalogue/products/{id}/images/order': {
    put: {
      summary: 'Reorder the extra images (every current extra image exactly once)',
      requestParams: idParam,
      requestBody: body(ReorderImagesSchema),
      responses: imageResponses,
    },
  },
  '/api/v1/catalogue/products/{id}/images/{imageId}': {
    delete: {
      summary: 'Remove the hero or an extra image',
      requestParams: { path: z.object({ id: z.uuid(), imageId: z.uuid() }) },
      responses: imageResponses,
    },
  },
  '/api/v1/catalogue/images/{imageId}': {
    get: {
      summary: 'The image file (any signed-in staff member; cached, never changes)',
      requestParams: { path: z.object({ imageId: z.uuid() }) },
      responses: {
        '200': { description: 'The image', content: imageBody },
        '401': unauthorized,
        '404': problemResponse('No such image'),
      },
    },
  },
  '/api/v1/catalogue/import': {
    post: {
      summary: 'Import the catalogue from CSV (all or nothing; idempotent by SKU; dryRun previews)',
      requestBody: body(ImportCatalogueSchema),
      responses: {
        '200': {
          description: 'Result (errors listed per line; nothing written when any)',
          content: json(ImportResultSchema),
        },
        '400': problemResponse('Invalid request'),
        '401': unauthorized,
        '403': forbidden,
      },
    },
  },
};
